import React, { useState } from 'react';
import { faChevronDown, faChevronRight, faClockRotateLeft, faFolderOpen, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ActionMenu from '../action-menu/ActionMenu';
import { useTranslation } from 'react-i18next';
import { getFlexiblePrecision, getTaskColorClass, getTaskStartDate, isAllDayTask, isStartSchedule } from './scheduleUtils';
import { getTaskScheduleLabel } from './taskScheduleLabel';
import { TaskCompletionButton } from './TaskCompletionButton';
import { PlannerTaskInfo } from './types';
import { OverdueRescheduleOption } from './scheduleViewTypes';
import { ScheduleRescheduleModal } from './ScheduleRescheduleModal';
import { ScheduleSubtasks } from './ScheduleSubtasks';

interface ScheduleTaskCardProps {
    getSourceLabel: (folderId: string, collectionId: string) => string;
    onDeleteTask: (taskId: string, collectionId: string) => void;
    onEditTask: (task: PlannerTaskInfo['task'], collectionId: string) => void;
    onOpenTask: (taskInfo: PlannerTaskInfo) => void;
    onOpenTaskInCollection: (taskInfo: PlannerTaskInfo) => void;
    onRescheduleTask: (taskInfo: PlannerTaskInfo, option: OverdueRescheduleOption, customTime?: string) => void;
    onToggleComplete: (taskId: string, collectionId: string) => void;
    onToggleSubtask: (taskId: string, collectionId: string) => void;
    isOverdue?: boolean;
    showScheduledDate?: boolean;
    taskInfo: PlannerTaskInfo;
}

export const ScheduleTaskCard: React.FC<ScheduleTaskCardProps> = ({
    getSourceLabel, onDeleteTask, onEditTask, onOpenTask, onOpenTaskInCollection, onRescheduleTask,
    isOverdue = false, onToggleComplete, onToggleSubtask, showScheduledDate = false, taskInfo,
}) => {
    const { task, folderId, collectionId, parentTitles } = taskInfo;
    const { t, i18n } = useTranslation();
    const [showReschedule, setShowReschedule] = useState(false);
    const [showSubtasks, setShowSubtasks] = useState(false);
    const taskDate = getTaskStartDate(task);
    const flexible = getFlexiblePrecision(task);
    const now = new Date();
    const hasStarted = isStartSchedule(task.schedule) && Boolean(taskDate && taskDate < now);
    const parentPath = parentTitles || [];
    const subtasks = task.subtasks || [];
    const completedSubtasks = subtasks.filter((subtask) => subtask.completed).length;
    return (
        <div className={`schedule-task ${getTaskColorClass(task)} ${task.completed ? 'completed' : ''}`}
            onClick={() => onOpenTask(taskInfo)}>
            <div className="schedule-task-row">
                <div className="schedule-task-content">
                    <TaskCompletionButton task={task} collectionId={collectionId} onToggleComplete={onToggleComplete} />
                    <div className="task-info">
                        <span className="task-title">{task.title}</span>
                        {parentPath.length > 0 && <span className="schedule-task-parent">
                            {t('dailyPlanner.subtaskOf', { parent: parentPath.join(' › ') })}
                        </span>}
                        <span className="schedule-task-meta">
                            <span className={`task-interval ${isOverdue ? 'is-overdue' : ''}`}>
                                {getTaskScheduleLabel(
                                    task, i18n.language, now.getFullYear(), undefined, {
                                        deadline: t('addTask.deadline'),
                                        startsAt: t(hasStarted ? 'dailyPlanner.startedAt' : 'dailyPlanner.startsAt'),
                                        startsIn: t(hasStarted ? 'dailyPlanner.startedIn' : 'dailyPlanner.startsIn'),
                                        startsOn: t(hasStarted ? 'dailyPlanner.startedOn' : 'dailyPlanner.startsOn'),
                                    }, now
                                )}
                            </span>
                            <span aria-hidden="true">·</span>
                            <span className="task-source">{getSourceLabel(folderId, collectionId)}</span>
                        </span>
                        {showScheduledDate && taskDate && !flexible && !isAllDayTask(task) && (
                            <span className="schedule-overdue-date">
                                {taskDate.toLocaleDateString(i18n.language, { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                        )}
                    </div>
                </div>
                <div className="schedule-task-actions">
                    {subtasks.length > 0 && (
                        <button type="button" className="schedule-subtask-toggle"
                            aria-expanded={showSubtasks}
                            aria-label={t('dailyPlanner.subtaskProgress', {
                                completed: completedSubtasks, total: subtasks.length,
                            })}
                            onClick={(event) => {
                                event.stopPropagation();
                                setShowSubtasks((current) => !current);
                            }}>
                            <span>{completedSubtasks}/{subtasks.length}</span>
                            <FontAwesomeIcon icon={showSubtasks ? faChevronDown : faChevronRight} />
                        </button>
                    )}
                    {showReschedule && (
                        <ScheduleRescheduleModal taskInfo={taskInfo} onClose={() => setShowReschedule(false)}
                            onReschedule={(option, time) => onRescheduleTask(taskInfo, option, time)} />
                    )}
                    <ActionMenu icon={undefined} buttonStyle={{}} actions={[
                        {
                            label: t('dailyPlanner.openInCollection'),
                            icon: faFolderOpen, callback: () => onOpenTaskInCollection(taskInfo),
                        },
                        ...(showScheduledDate ? [{
                            label: t('dailyPlanner.reschedule'), icon: faClockRotateLeft,
                            callback: () => setShowReschedule(true),
                        }] : []),
                        { label: t('dailyPlanner.editTask'), icon: faPen, callback: () => onEditTask(task, collectionId) },
                        { label: t('dailyPlanner.deleteTask'), icon: faTrash, callback: () => onDeleteTask(task.id, collectionId) },
                    ]} />
                </div>
            </div>
            {showSubtasks && subtasks.length > 0 && (
                <ScheduleSubtasks subtasks={subtasks} collectionId={collectionId}
                    onToggleSubtask={onToggleSubtask} />
            )}
        </div>
    );
};
